/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Observable } from "@vienna-hypertext/observable";

import { Asset, AssetType, JobStatus, Message } from "@vienna/common";

import { Plate } from "../Plate";
import { API } from "src/classes/api";
import { GeneratorSocket } from "src/classes/plate/Generator/GeneratorSocket";
import { Logger } from "src/classes/plate/Generator/Logger/Logger";

import { AssetHandler } from "./AssetHandler";
import { ExampleData } from "./example-data";
export type PlateGeneratorSnaphshot = {
  assets: Asset[];
  messages: Message[];
  jobStatus: JobStatus;
  heartbeatCount: number;
  topic: string;
  selectedAssetTypes: AssetType[];
};

export class PlateGenerator extends Observable<PlateGeneratorSnaphshot> {
  private heartbeatTimeoutId: ReturnType<typeof setTimeout> | null = null;

  public static HEARTBEAT_TIMEOUT: number = 6_000;

  socket: GeneratorSocket;

  logger: Logger;

  jobId: string;

  plate: Plate;

  assetHandler: AssetHandler;

  private constructor(snapshot: PlateGeneratorSnaphshot, plate: Plate) {
    super(snapshot);
    this.plate = plate;
    this.jobId = this.plate.snapshot.id;
    this.logger = Logger.create();
    this.socket = new GeneratorSocket(this);
    this.assetHandler = new AssetHandler(plate);
  }

  /* -------------------------------------------------- */
  /*                    API Requests                    */
  /* -------------------------------------------------- */
  public async generate(topic: string): Promise<void> {
    this.onJobStart();
    const res = await API.plates.generate(topic, this.snapshot.selectedAssetTypes, this.jobId);
    this.onJobEnd(res);
  }

  public async cancelJob(): Promise<void> {
    this.onJobEnd({ status: JobStatus.Canceled });
    await API.plates.cancelJob(this.jobId);
  }

  public async abortJob(errorMessage: string): Promise<void> {
    this.onJobEnd({ status: JobStatus.Failed, errorMessage });
    await API.plates.cancelJob(this.jobId);
  }

  public async handleArbitraryInput(input: string): Promise<void> {
    this.logger.addUserMessage(input);
    this.onJobStart();
    const context = { topic: this.plate.snapshot.title, previousMessages: this.logger.messages };
    await API.plates.handleArbitraryInput(input, context, this.jobId);
    this.onJobEnd({ status: JobStatus.Success });
  }

  /* -------------------------------------------------- */
  /*                  Action Confirmation                */
  /* -------------------------------------------------- */
  public handleConfirmation(confirmed: boolean, logId: string): void {
    this.socket.emitConfirmation(confirmed, logId);
    this.logger.handleConfirmation();
  }

  public toggleSelectedAsset(assetType: AssetType): void {
    if (this.snapshot.selectedAssetTypes.includes(assetType)) {
      this.setState({ selectedAssetTypes: this.snapshot.selectedAssetTypes.filter((type) => type !== assetType) });
    } else {
      this.setState({ selectedAssetTypes: [...this.snapshot.selectedAssetTypes, assetType] });
    }
  }

  /* -------------------------------------------------- */
  /*                        State                       */
  /* -------------------------------------------------- */
  public addAsset(asset: Asset): void {
    this.assetHandler.createBlockFromAsset(asset);
    this.setState({ assets: [...this.snapshot.assets, asset] });
  }

  public updateTopic(topic: string): void {
    this.setState({ topic });
  }

  /* -------------------------------------------------- */
  /*                  Job Monitoring                    */
  /* -------------------------------------------------- */

  private onJobStart(): void {
    this.setState({ jobStatus: JobStatus.Running });
    this.logger.onJobStart();
    this.handleHeartbeat(); // Start waiting for the first heartbeat
  }

  public handleHeartbeat(): void {
    this.setState({ heartbeatCount: this.snapshot.heartbeatCount + 1 });
    // Clear the previous timer if it exists
    if (this.heartbeatTimeoutId) {
      clearTimeout(this.heartbeatTimeoutId);
    }
    // Start a new timer. If it expires, consider the job as failed.
    this.heartbeatTimeoutId = setTimeout(() => {
      this.abortJob("Connection with the server was lost.");
    }, PlateGenerator.HEARTBEAT_TIMEOUT);
  }

  private onJobEnd(res: { status: JobStatus; messages?: Message[]; errorMessage?: string | null }): void {
    const shouldLog = res.status !== this.snapshot.jobStatus;
    this.logger.onJobEnd(res, shouldLog);
    this.setState({ jobStatus: res.status, messages: res.messages || [] });
    // Clear the heartbeat timer when the job ends
    if (this.heartbeatTimeoutId) {
      clearTimeout(this.heartbeatTimeoutId);
      this.heartbeatTimeoutId = null;
    }
  }

  /* -------------------------------------------------- */
  /*                   Static Methods                   */
  /* -------------------------------------------------- */
  public static create(plate: Plate): PlateGenerator {
    const snapshot: PlateGeneratorSnaphshot = {
      assets: [],
      messages: [],
      jobStatus: JobStatus.None,
      heartbeatCount: 0,
      topic: "",
      selectedAssetTypes: [],
    };
    return PlateGenerator.fromSnapshot(snapshot, plate);
  }

  public static example(plate: Plate): PlateGenerator {
    const snapshot: PlateGeneratorSnaphshot = {
      assets: ExampleData.assets,
      messages: ExampleData.messages,
      jobStatus: JobStatus.None,
      heartbeatCount: 0,
      topic: "",
      selectedAssetTypes: Object.values(AssetType),
    };
    return PlateGenerator.fromSnapshot(snapshot, plate);
  }

  public static fromSnapshot(snapshot: PlateGeneratorSnaphshot, plate: Plate): PlateGenerator {
    return new PlateGenerator(snapshot, plate);
  }

  /* -------------------------------------------------- */
  /*                      Destroy                       */
  /* -------------------------------------------------- */
  public destroy(): void {
    this.socket.destroy();
    this.logger.destroy();
  }
}
